<template>
  <div>
    <b-button class="feeback-button" :title="$t('feedbackModal.button')" @click="openFeedback">
      <b-tooltip :label="$t('feedbackModal.button')" type="is-black" position="is-right">
        <b-icon icon="question"></b-icon>
      </b-tooltip>
    </b-button>

    <ValidationObserver ref="observer" v-slot="{ handleSubmit }">
      <b-modal
        v-model="display"
        has-modal-card
        trap-focus
        scroll="keep"
        aria-role="dialog"
        aria-label="Feedback modal"
        :aria-modal="true"
        class="FeedbackModal"
      >
        <div class="modal-card">
          <header class="modal-card-head">
            <H3TitleView class="modal-card-title">{{ $t("feedbackModal.title") }}</H3TitleView>
            <button
              type="button"
              class="delete"
              aria-label="close"
              @click="display = false"
            ></button>
          </header>
          <section class="modal-card-body">
            <b-tabs
              v-model="currentTabIndex"
              type="is-toggle"
              :animated="false"
              class="FeedbackModal-tabs"
            >
              <b-tab-item :label="$t('feedbackModal.help')"> </b-tab-item>
              <b-tab-item :label="$t('feedbackModal.support')">
                <b-message type="is-info">
                  {{ $t("feedbackModal.feedback-guide") }}
                  <p v-if="model.userId">
                    {{ $t("feedbackModal.account-modification-guide") }}
                    <a @click="showPersonnalAccount">{{ $t("profile") }}</a>
                  </p>
                </b-message>

                <InputWithValidation
                  v-model="model.userFullName"
                  rules="required"
                  :label="$t('feedbackModal.user-name')"
                  vid="userFullName"
                  :disabled="model.userId"
                />

                <InputWithValidation
                  v-model="model.email"
                  rules="required|email"
                  :label="$t('profilePage.saisir-courriel')"
                  vid="email"
                  :disabled="model.userId"
                />

                <InputWithValidation
                  v-model="model.phone"
                  rules="validPhoneNumber"
                  :label="$t('profilePage.saisir-telephone')"
                  vid="phoneNumber"
                />

                <b-field :label="$t('feedbackModal.categories.label')">
                  <b-radio
                    v-for="category in categories"
                    :key="category.id"
                    v-model="model.category"
                    name="category"
                    :native-value="category.name"
                    >{{ category.name }}
                  </b-radio>
                </b-field>

                <b-message v-if="model.category" type="is-info">
                  <span class="feedback-doc">{{ $t("feedbackModal.consult-doc-before") }}</span>
                  <PDFLinkWithModal
                    class="feedback-doc"
                    file-name="SAGAE_Guide_utilisateur"
                    :link-text="$t('userDocumentation')"
                  />
                  <span class="feedback-doc"> {{ $t("feedbackModal.consult-doc-after") }}</span>
                </b-message>

                <InputWithValidation
                  v-model="model.subject"
                  :label="$t('feedbackModal.subject')"
                  vid="subject"
                />

                <InputWithValidation
                  v-model="model.description"
                  rules="required"
                  type="textarea"
                  vid="description"
                  :label="$t('feedbackModal.issue-description')"
                />

                <b-checkbox v-model="withPicture">
                  {{ $t("feedbackModal.include-screenshot") }}
                </b-checkbox>
              </b-tab-item>
            </b-tabs>
          </section>
          <footer class="modal-card-foot">
            <div class="hoverable-discrete-link mr-3" @click="closeFeedback">
              {{ $t("close-generic") }}
            </div>
            <b-button
              v-if="currentTabIndex === 1"
              :label="$t('feedbackModal.send')"
              type="is-primary"
              @click="handleSubmit(sendClicked)"
            />
          </footer>
        </div>
      </b-modal>
    </ValidationObserver>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue"
import { LoginService } from "@/services/rest/login/LoginService"
import { FeedbackService } from "@/services/rest/utils/FeedbackService"
import InputWithValidation from "@/views/inputs/InputWithValidation.vue"
import { FeedbackDTO } from "@/model/bean/GeneratedDTOs"
import ValidationObserver from "@/services/validation/ValidationObserver.vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import html2canvas from "html2canvas"
import PDFLinkWithModal from "@/views/commons/PDFLinkWithModal.vue"
import H3TitleView from "../commons/H3TitleView.vue"
import {
  BButton,
  BMessage,
  BIcon,
  BModal,
  BTooltip,
  BField,
  BCheckbox,
  BRadio,
  BTabs,
  BTabItem,
} from "buefy"

export default defineComponent({
  name: "FeedbackModal",
  components: {
    InputWithValidation,
    ValidationObserver,
    PDFLinkWithModal,
    H3TitleView,
    BButton,
    BField,
    BRadio,
    BMessage,
    BIcon,
    BTooltip,
    BModal,
    BTabs,
    BTabItem,
    BCheckbox,
  },
  data() {
    return {
      feedbackService: FeedbackService.INSTANCE,
      loginService: LoginService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,

      display: false,

      version: process.env.VUE_APP_VERSION,
      projectVersion: process.env.VUE_APP_PROJECT_VERSION,
      gitRevision: process.env.VUE_APP_GIT_REVISION,

      model: new FeedbackDTO(),
      withPicture: false,
      userFullName: "",

      currentTabIndex: 0,
    }
  },
  computed: {
    frontendVersion(): string {
      return `${this.projectVersion} (${this.gitRevision})`
    },
    categories(): any[] {
      return [
        { id: "BUG", name: this.$t("feedbackModal.categories.bug") },
        { id: "LOST", name: this.$t("feedbackModal.categories.lost") },
      ]
    },
  },
  methods: {
    openFeedback(): void {
      this.display = true
    },

    sendClicked(): void {
      this.addNavigationContext()
      this.addUserEnvInfo()
      // this.model.device = this.device;
      this.display = false

      if (this.withPicture) {
        const rootElement: HTMLElement = this.castRootElement(document.querySelector("body"))
        const modal: HTMLElement = this.castRootElement(document.querySelector(".modal"))
        modal.classList.add("hidden")
        html2canvas(rootElement, {
          x: window.pageXOffset,
          y: window.pageYOffset,
          width: window.innerWidth,
          height: window.innerHeight,
        }).then((canvas: any) => {
          this.model.screenshot = canvas.toDataURL("image/png")
          this.sendFeedback()
          modal.classList.remove("hidden")
        })
      } else {
        this.sendFeedback()
      }
    },

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    castRootElement(whatever: any): HTMLElement {
      return whatever
    },

    sendFeedback(): void {
      this.feedbackService.sendFeedback(this.model).then(() => {
        this.infoReportingService.success(this.$t("feedbackModal.feedback-sent").toString())
      })
    },

    closeFeedback(): void {
      this.display = false
    },

    reinitializeForm(): void {
      this.model = new FeedbackDTO()
      this.model.category = this.categories[0].name
      this.withPicture = false
    },

    loadProfile(): void {
      const logged = this.loginService.getLoggedUser()
      if (logged != undefined && logged.id != undefined) {
        this.model.userId = logged.id
        this.model.userFullName = logged.prenom + " " + logged.nom
        this.model.email = logged.courriel
      }
    },

    addNavigationContext(): void {
      this.model.date = new Date()
      this.model.location = window.location.href
      this.model.locationTitle = window.document.title
      this.model.frontendVersion = this.frontendVersion
    },

    addUserEnvInfo(): void {
      this.model.browser = this.getBrowserNameAndVersion()
      this.model.os = this.getOperatingSystemNameAndVersion()
      this.model.platform = navigator.platform
      this.model.screenResolution = screen.width + "x" + screen.height
      this.model.displaySize = window.innerWidth + "x" + window.innerHeight
      this.model.devicePixelRatio = "" + window.devicePixelRatio
      this.model.locale = navigator.language
    },

    getOperatingSystemNameAndVersion(): string {
      const userAgent = navigator.userAgent
      let os = userAgent
      if (userAgent.indexOf("Windows NT 10.0") != -1) os = "Windows 10"
      if (userAgent.indexOf("Windows NT 6.3") != -1) os = "Windows 8.1"
      if (userAgent.indexOf("Windows NT 6.2") != -1) os = "Windows 8"
      if (userAgent.indexOf("Windows NT 6.1") != -1) os = "Windows 7"
      if (userAgent.indexOf("Windows NT 6.0") != -1) os = "Windows Vista"
      if (userAgent.indexOf("Windows NT 5.1") != -1) os = "Windows XP"
      if (userAgent.indexOf("Windows NT 5.0") != -1) os = "Windows 2000"
      if (userAgent.indexOf("Mac") != -1) os = "Mac/iOS"
      if (userAgent.indexOf("X11") != -1) os = "UNIX"
      if (userAgent.indexOf("Linux") != -1) os = "Linux"
      if (userAgent.indexOf("Android") != -1) os = "Android"
      return os
    },

    getBrowserNameAndVersion(): string {
      const ua = navigator.userAgent
      let tem,
        M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || []
      if (/trident/i.test(M[1])) {
        tem = /\brv[ :]+(\d+(\.\d+)?)/g.exec(ua) || []
        return "IE " + (tem[1] || "")
      }
      M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"]
      if ((tem = ua.match(/version\/(\d+)/i)) != null) M[2] = tem[1]
      return M.join(" ")
    },

    showPersonnalAccount(): void {
      // Method called from template but not implemented in class
    },
  },
})
</script>

<style lang="scss" scoped>
@import "@/styles/variables";

.modal.hidden {
  display: none;
}

.modal-card-body {
  max-height: 80vh;
  overflow-y: auto !important;

  .b-radio {
    padding-right: 20px;
  }
}

.button.feeback-button {
  justify-content: center;
  transform: scale(0.9);
  transition: all 1s ease;
  border: 0;
  background: transparent linear-gradient(to bottom left, #f06595, #c2255c);
  box-shadow: 0 0 20px #0006;
  border-radius: 50%;
  width: $feedback-button-width;
  height: $feedback-button-width;
  z-index: 20;

  .icon {
    color: #fff;
    font-size: 1.5rem;
    vertical-align: middle;
  }

  &:hover {
    transform: scale(1);
    background: linear-gradient(to bottom left, #f06595, #f06595);
    box-shadow: 0 0 20px #0005;
  }
}
</style>

<style lang="scss">
.button.feeback-button {
  .b-tooltip .tooltip-content {
    font-size: 16px;
    left: calc(100% + 30px);
    padding-left: 30px;
    padding-right: 30px;
  }
}

.feedback-doc {
  float: left;
  padding-right: 8px;
}

.FeedbackModal {
  &.modal .modal-card {
    height: 75%;
  }

  .animation-content {
    height: 100%;
  }
}

.FeedbackModal-tabs {
  &.b-tabs .tab-content {
    height: 100%;
    align-items: center;
    justify-content: center;
  }
}
</style>
